<template>
  <Teleport
    to="#global-drawer"
    :disabled="!hasTarget || disableTeleport"
  >
    <!-- Overlay, tint the page. We use a rare bg-black class here, because we don't want dark modes to overlay with a lighter colour. -->
    <div
      :class="isDrawerOpen ? ' opacity-40 delay-75' : 'opacity-0  pointer-events-none' "
      class="fixed inset-0 z-40 h-screen w-screen overflow-hidden cursor-pointer duration-150 delay-0 transition-opacity bg-black"
      data-test-id="global-drawer-overlay"
      @click="handleCloseClick"
    />

    <!-- the main drawer itself -->
    <div
      ref="drawerRef"
      :class="isDrawerOpen ? 'translate-x-0' : 'translate-x-full'"
      class="fixed rounded-l-3xl bg-surface-light flex flex-col text-sm right-0 top-0 z-50 h-full max-h-screen w-full max-w-[90%] sm:max-w-[480px] rounded-tl-lg  rounded-bl-[20px] duration-300 ease-out transition-transform"
      data-test-id="global-drawer"
      @transitionend="onPanelTransitionEnd"
    >
      <!-- Header -->
      <div
        class="flex items-start bg-surface-light rounded-tl-2xl justify-between pr mt-12 sm:mt-0 pl-4 md:pl-6 pr-8 py-6"
        :class="
          [
            drawerTitle ? '' : '',
          ]"
      >
        <div
          class="flex items-center flex-row select-none flex-1"
          :class="showDetailView ? 'cursor-pointer' : null "
          @click="showDetailView ? handleBackClick() : null "
        >
          <AM3Icon
            v-if="drawerIcon && !hasHeaderSlot"
            :type="showDetailView ? 'chevron-left' : drawerIcon"
            :size="showDetailView ? 'md' : 'md'"
            class="text-primary "
          />
          <div
            :class="drawerIcon && !hasHeaderSlot ? 'ml-2' : ''"
            class="flex-1"
          >
            <!-- Slot for custom header content (always rendered for Teleport target) -->
            <div
              v-show="hasHeaderSlot"
              id="drawer-header"
            />
            <!-- Fallback to default header if no slot content -->
            <div v-show="!hasHeaderSlot">
              <h2
                class="font-bold text-primary duration-75 transition-all text-2xl"
                v-text="drawerTitle"
              />
              <p
                v-if="drawerSubtitle"
                class="text-sm text-secondary mt-1"
                v-text="drawerSubtitle"
              />
            </div>
          </div>
        </div>
        <!-- Close button -->
        <button
          class="rounded-full size-8 outline-offset-2 focus:outline-brand relative hover:bg-surfaces-neutral hover:text-brand transition-colors duration-75 flex flex-row items-center justify-center"
          data-test-id="button--close-global-drawer"
          aria-label="Close filter panel"
          @click="handleCloseClick"
        >
          <span class="[@media(pointer:fine)]:hidden top-1/3 left-1/3 -translate-x-1/3 -translate-y-1/3 size-10 absolute" />
          <AM3Icon
            type="x-mark"
            size="lg"
            color="strong"
          />
        </button>
      </div>

      <!-- Body -->
      <div class="relative overflow-x-hidden flex flex-1 flex-col">
        <!-- Main Content View -->
        <div
          class="absolute [&>div]:pb-32 [&>div]:px-4 [&>div]:md:px-6 [&>form]:pb-32 [&>form]:px-4 [&>form]:md:px-6 flex flex-col flex-1 inset-0 z-30 overflow-y-auto"
          :class="[
            showDetailView
              ? '-translate-x-full'
              : 'translate-x-0',
          ]"
        >
          <component
            :is="componentToRender"
            v-bind="componentProps"
            :is-drawer-open="isDrawerOpen"
            @show-detail="handleShowDetail"
          />
        </div>

        <!-- Detail Content View -->
        <div
          class="absolute inset-0 z-40"
          :class="[
            showDetailView
              ? 'translate-x-0'
              : 'translate-x-full',
          ]"
        >
          <!-- Detail Content Slot -->
          <div class="pb-4 h-full overflow-y-auto [&>div:first-child]:pb-20 [&>div]:px-4 [&>div]:md:px-6 [&>form:first-child]:pb-20 [&>form]:px-4 [&>form]:md:px-6 flex flex-col items-stretch justify-between min-h-[500px] max-h-[calc(100vh-6rem)] bg-surface-light">
            <component
              :is="detailComponent"
              v-if="detailComponent"
              v-bind="detailComponentProps"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="fixed z-50 rounded-bl-3xl bottom-0 left-0 right-0 bg-[#ffffff88] backdrop-blur-sm has-[button]:border-t-2 border-solid border-surface"
      >
        <div
          id="drawer-footer"
          class="w-full max-w-1/2 top-auto bottom-0  has-[button]:py-8 px-4 md:px-6"
        >
        <!-- Footer content can be added here -->
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick, inject } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useEscapeToClose } from '@/composables/useEscapeToClose'

// Try to get the store from the injection, but make it optional
const store = inject('store', null)
const router = useRouter()
const route = useRoute()
const hasTarget = ref(false)
const drawerRef = ref<HTMLElement | null>(null)

// Add props for direct control when store is not available
const props = defineProps({
  // These props are only used when the store is not available
  open: {
    type: Boolean,
    default: undefined,
  },
  title: {
    type: String,
    default: undefined,
  },
  component: {
    type: [Object, String],
    default: undefined,
  },
  componentName: {
    type: String,
    default: undefined,
  },
  props: {
    type: Object,
    default: () => ({}),
  },
  // For Storybook, allow disabling teleport to fix rendering issues
  disableTeleport: {
    type: Boolean,
    default: false,
  },
})

// Add events for direct control
const emit = defineEmits(['close'])

const checkTarget = () => {
  hasTarget.value = !!document.getElementById('global-drawer')
}

onMounted(() => {
  checkTarget()
  // Re-check when DOM updates
  window.addEventListener('DOMContentLoaded', checkTarget)
})

onBeforeUnmount(() => {
  window.removeEventListener('DOMContentLoaded', checkTarget)
})

const isDrawerOpen = computed(() => store?.getters?.['globalDrawerModule/isDrawerOpen'] || !!props.open)
const drawerTitle = computed(() => store?.getters?.['globalDrawerModule/drawerTitle'] || props.title)
const drawerIcon = computed(() => store?.getters?.['globalDrawerModule/drawerIcon'] || '')
const drawerSubtitle = computed(() => store?.getters?.['globalDrawerModule/drawerSubtitle'] || '')
const drawerComponentName = computed(() => store?.getters?.['globalDrawerModule/drawerComponentName'] || props.componentName)
const drawerComponent = computed(() => store?.getters?.['globalDrawerModule/drawerComponent'] || props.component)
const componentProps = computed(() => store?.getters?.['globalDrawerModule/drawerComponentProps'] || props.props)
const hasHeaderSlot = computed(() => store?.getters?.['globalDrawerModule/hasHeaderSlot'] || false)

// Detail view state
const showDetailView = ref(false)
const detailComponent = ref<any>(null)
const detailComponentProps = ref<Record<string, any>>({})
const originalTitle = ref('')

// Computed property to determine what to render
// If we have a component instance, use that. If not, fallback to the component name or a div.
const componentToRender = computed(() => {
  return drawerComponent.value || drawerComponentName.value || 'div'
})

useEscapeToClose(isDrawerOpen, () => handleCloseClick())

/**
 * @name handleCloseClick
 * Toggle the global drawer open or closed when the cancel button is clicked.
 * 💡 We aren't using emits from the child components, it's all handled via SET_IS_DRAWER_OPEN.
 */
function handleCloseClick() {
  // Trigger the slide-out only — detail/header state is cleared in
  // onPanelTransitionEnd after the animation completes, so content doesn't
  // flicker mid-slide.
  if (store && 'commit' in store) {
    store.commit('globalDrawerModule/SET_IS_DRAWER_OPEN', false)
  }
  else {
    // Emit an event if store is not available
    emit('close')
  }
}

// Drop drawer content once the slide-out animation has completed.
// Guards: only react to the panel's own transform transition, and only
// when the drawer is now in the closed state.
function onPanelTransitionEnd(event: TransitionEvent) {
  if (event.target !== drawerRef.value) return
  if (event.propertyName !== 'transform') return
  if (isDrawerOpen.value) return
  showDetailView.value = false
  detailComponent.value = null
  detailComponentProps.value = {}
  if (store && 'commit' in store) {
    store.commit('globalDrawerModule/SET_HAS_HEADER_SLOT', false)
  }
}

// Handle showing detail view
function handleShowDetail(payload) {
  // Ensure we're setting an actual component, not a string
  if (payload.component && typeof payload.component === 'object') {
    detailComponent.value = payload.component
  }
  else {
    console.error('Invalid component passed to handleShowDetail:', payload.component)
    return
  }

  detailComponentProps.value = payload.props || {}
  showDetailView.value = true
}

// Handle back button in detail view
function handleBackClick() {
  showDetailView.value = false
  detailComponent.value = null
  detailComponentProps.value = {}
}

// Watch for showDetailView to restore title when going back
watch(showDetailView, (isShowing) => {
  if (!isShowing && originalTitle.value) {
    // Restore the original title when detail view closes
    if (store && 'commit' in store) {
      store.commit('globalDrawerModule/SET_DRAWER_TITLE', originalTitle.value)
    }
  }
})

/**
 * Reset drawer state when route changes
 */
function resetDrawer() {
  if (store && 'commit' in store) {
    store.commit('globalDrawerModule/SET_IS_DRAWER_OPEN', false)
    store.commit('globalDrawerModule/SET_DRAWER_TITLE', '')
    store.commit('globalDrawerModule/SET_DRAWER_SUBTITLE', '')
    store.commit('globalDrawerModule/SET_DRAWER_ICON', '')
    store.commit('globalDrawerModule/SET_DRAWER_COMPONENT', undefined)
    store.commit('globalDrawerModule/SET_DRAWER_COMPONENT_NAME', '')
    store.commit('globalDrawerModule/SET_DRAWER_COMPONENT_PROPS', {})
    store.commit('globalDrawerModule/SET_HAS_HEADER_SLOT', false)
    store.commit('globalDrawerModule/SET_IS_DRAWER_OPEN', false)
  }
  else {
    // Emit an event if store is not available
    emit('close')
  }
}

// Watch for route changes to close drawer
// Only close when the path changes, not when query params change (e.g., applying filters)
watch(() => route.path, (newPath, oldPath) => {
  if (newPath !== oldPath && isDrawerOpen.value) {
    resetDrawer()
  }
})

// Watch for drawer opening and focus the first input element
watch([isDrawerOpen, drawerRef], ([isOpen, drawer]) => {
  if (isOpen && drawer) {
    // Store original title only once when drawer first opens
    if (!originalTitle.value) {
      if (store && 'getters' in store) {
        originalTitle.value = store.getters['globalDrawerModule/drawerTitle']
      }
      else {
        originalTitle.value = props.title || ''
      }
    }

    // Reset detail view to default when drawer opens
    showDetailView.value = false
    detailComponent.value = null
    detailComponentProps.value = {}

    // Limit scroll while the drawer is open.  This prevents double scrollbars etc.
    document.querySelector('#__nuxt > .app')?.classList.add('max-h-screen')
    document.querySelector('#__nuxt > .app')?.classList.add('overflow-y-hidden')

    // Focus the first input element inside the drawer
    nextTick(() => {
      const firstInput = drawer.querySelector('input, textarea, select, [contenteditable="true"]')

      if (firstInput) {
        (firstInput as HTMLElement).focus()
      }
    })
  }
  else {
    // Reset visible title back to original when drawer closes
    if (originalTitle.value && store && 'commit' in store) {
      store.commit('globalDrawerModule/SET_DRAWER_TITLE', originalTitle.value)
    }

    // Remove scroll limits
    document.querySelector('#__nuxt > .app')?.classList.remove('max-h-screen')
    document.querySelector('#__nuxt > .app')?.classList.remove('overflow-y-hidden')
  }
}, { immediate: true })
</script>
